import type { FocusEventHandler, KeyboardEventHandler } from 'react';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import {
  CaretLeftIcon,
  CaretLeftStopIcon,
  CaretRightIcon,
  CaretRightStopIcon,
} from '../../images/shapes';

import Input from '../Input/Input';

const paginationDefaultPropOnPageChange = /* istanbul ignore next */ () => true;

export type PageChangeFn = (pageNumber: number) => void;

interface PaginationProps {
  /**
   * Current page number
   */
  page?: number;
  /**
   * Number of pages
   */
  numOfPages?: number;
  /**
   * Additional classes for pagination box
   */
  additionalClasses?: string;
  /**
   * Page change function
   */
  onPageChange?: PageChangeFn;
  /**
   * Component test id
   */
  testId?: string;
}

const Pagination = ({
  additionalClasses = '',
  page = 1,
  numOfPages = 1,
  onPageChange = paginationDefaultPropOnPageChange,
  testId = '',
}: PaginationProps) => {
  const [focusPagination, setFocusPagination] = useState(false);
  const paginationInputRef = useRef<HTMLInputElement>(null);
  const currentPage = useMemo(
    () => Math.min(Math.max(page, 1), numOfPages),
    [page, numOfPages],
  );

  useEffect(() => {
    if (focusPagination && paginationInputRef.current) {
      paginationInputRef.current.focus();
    }
  }, [focusPagination]);
  const handlePageChange = useCallback(
    (pageNumber: number) => {
      if (pageNumber < 1 || pageNumber > numOfPages) {
        return;
      }
      if (pageNumber !== page) {
        onPageChange(pageNumber);
      }
    },
    [numOfPages, onPageChange, page],
  );

  const handleOnEnterSubmit = useCallback<
    KeyboardEventHandler<HTMLInputElement>
  >(
    (event) => {
      if (event.key === 'Enter') {
        const result = parseInt((event.target as HTMLInputElement).value);
        handlePageChange(!isNaN(result) ? result : 1);
        setFocusPagination(false);
      }
    },
    [handlePageChange],
  );

  const handleOnBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (event) => {
      const result = parseInt(event.target.value);
      handlePageChange(!isNaN(result) ? result : 1);
      setFocusPagination(false);
    },
    [handlePageChange],
  );

  const currentPageInput = useMemo(
    () => (
      <div
        className={twMerge('mx-1 md:mx-2', !focusPagination && 'my-2')}
        {...getTestProps(testId, 'page-no-mode-swap')}
      >
        {focusPagination ? (
          <Input
            type={'number'}
            value={currentPage}
            onBlur={handleOnBlur}
            additionalInputClasses={twMerge(
              'text-blue-600 bg-transparent! border-transparent px-2 h-8 w-fit',
            )}
            onKeyDown={handleOnEnterSubmit}
            min="1"
            max={numOfPages}
            refs={paginationInputRef}
            {...getTestProps(testId, 'page-no', 'testId')}
          />
        ) : (
          <button
            type="button"
            onClick={() => setFocusPagination(true)}
            className={twMerge(
              'px-1 cursor-pointer text-blue-600 dark:text-blue-400',
            )}
            {...getTestProps(testId, 'page-no-mode-swap-button')}
          >
            {currentPage}
          </button>
        )}
      </div>
    ),
    [
      focusPagination,
      currentPage,
      handleOnBlur,
      handleOnEnterSubmit,
      numOfPages,
      testId,
    ],
  );

  const goToPrevPage = useCallback(() => {
    handlePageChange(currentPage - 1);
  }, [handlePageChange, currentPage]);

  const goToNextPage = useCallback(() => {
    handlePageChange(currentPage + 1);
  }, [handlePageChange, currentPage]);

  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  const goToLastPage = useCallback(() => {
    handlePageChange(numOfPages);
  }, [handlePageChange, numOfPages]);

  const { t } = useTranslation();

  return (
    <div
      className={twMerge(
        'px-1 py-3 flex items-center justify-center sm:px-6 space-x-12 select-none',
        additionalClasses,
      )}
      {...getTestProps(testId, 'wrapper')}
    >
      <nav
        className={twMerge(
          'relative z-0 inline-flex',
          'text-sm md:text-base font-medium leading-none flex items-center space-x-1 md:space-x-3',
        )}
        aria-label="Pagination"
      >
        <div className="flex items-center space-x-1 md:space-x-3">
          <button
            type="button"
            onClick={goToFirstPage}
            disabled={currentPage <= 1}
            className={twMerge(
              currentPage <= 1
                ? 'opacity-40 select-none'
                : 'cursor-pointer hover:underline',
            )}
            {...getTestProps(testId, 'left-end-arrow')}
          >
            <CaretLeftStopIcon
              className="h-3 text-slate-600 dark:text-slate-400"
              title={t('Pagination.FirstPage')}
            />
          </button>
          <button
            type="button"
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
            className={twMerge(
              'flex items-center font-medium',
              currentPage <= 1
                ? 'opacity-40 select-none'
                : 'cursor-pointer hover:underline',
            )}
            {...getTestProps(testId, 'left-arrow')}
          >
            <CaretLeftIcon
              className="h-3 text-slate-600 dark:text-slate-400"
              title={t('Pagination.PreviousPage')}
            />
          </button>
        </div>
        <div className="flex items-center px-2 dark:text-slate-200">
          <span className="hidden sm:inline sm:text-xs md:text-sm 2xl:text-base">
            {t('Pagination.Page')}
          </span>
          {currentPageInput}
          {t('Pagination.NumOfPages', { numOfPages })}
        </div>
        <div className="flex items-center space-x-1 md:space-x-4">
          <button
            type="button"
            onClick={goToNextPage}
            disabled={numOfPages === currentPage}
            className={twMerge(
              'flex items-center font-medium',
              numOfPages === currentPage
                ? 'opacity-50 select-none'
                : 'cursor-pointer hover:underline',
            )}
            {...getTestProps(testId, 'right-arrow')}
          >
            <CaretRightIcon
              className="h-3 text-slate-600 dark:text-slate-400"
              title={t('Pagination.NextPage')}
            />
          </button>
          <button
            type="button"
            onClick={goToLastPage}
            disabled={numOfPages === currentPage}
            className={twMerge(
              numOfPages === currentPage
                ? 'opacity-50 select-none'
                : 'cursor-pointer hover:underline',
            )}
            {...getTestProps(testId, 'right-end-arrow')}
          >
            <CaretRightStopIcon
              className="h-3 text-slate-600 dark:text-slate-400"
              title={t('Pagination.LastPage')}
            />
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Pagination;
